import { useEffect, useMemo } from "react";
import {
  useSubscriptions,
  type Subscription,
} from "@/features/settings/viewModal/useSubscriptions";

type SubscriptionCard = {
  key: string;
  subscriptionId: string;
  propertyAddress: string;
  propertyId: string;
  isActive: boolean;
  isCanceling: boolean;
  renewalDateLabel: string;
  annualFeeLabel: string;
  onCancel: () => Promise<void>;
};

const formatDate = (isoDate?: string): string => {
  if (!isoDate) {
    return "-";
  }
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const formatAnnualFee = (value?: string | number): string => {
  if (typeof value === "number") {
    return `$${value}`;
  }
  if (typeof value === "string" && value.trim()) {
    return value.startsWith("$") ? value : `$${value}`;
  }
  return "$99";
};

const normalizeSubscriptionStatus = (status: unknown): string | undefined => {
  if (typeof status === "string") {
    return status.toLowerCase();
  }
  if (typeof status === "boolean") {
    return status ? "active" : "inactive";
  }
  if (typeof status === "number") {
    return String(status);
  }
  return undefined;
};

const isActiveSubscription = (status: unknown): boolean => {
  const normalized = normalizeSubscriptionStatus(status);
  return normalized === "active" || normalized === "trialing";
};

type NestedSubscription = {
  subscription_id?: string;
  subscription_amount?: number | string;
  status_active?: unknown;
  current_period_end?: string;
};

type NestedProperty = {
  property_id?: string;
  address?: {
    full_address?: string;
  };
};

const getSubscriptionNode = (item: Subscription): NestedSubscription => {
  const nested = item.subscription as NestedSubscription | undefined;
  if (nested && typeof nested === "object") {
    return {
      ...nested,
      status_active: normalizeSubscriptionStatus(nested.status_active),
    };
  }

  return {
    subscription_id: item.subscription_id,
    subscription_amount: item.annualFee,
    status_active: normalizeSubscriptionStatus(item.status_active ?? item.status),
    current_period_end: item.currentPeriodEnd,
  };
};

const getPropertyNode = (item: Subscription): NestedProperty => {
  const nested = item.property as NestedProperty | undefined;
  if (nested && typeof nested === "object") {
    return nested;
  }

  return {
    property_id: item.propertyId,
    address: {
      full_address: item.propertyAddress,
    },
  };
};

export const useSettingsAutopaySubscriptionViewModel = () => {
  const { subscriptions, loading, cancelingId, refresh, cancelSubscription } = useSubscriptions();

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const cards = useMemo<SubscriptionCard[]>(() => {
    return subscriptions.map((item, index) => {
      const subscription = getSubscriptionNode(item);
      const property = getPropertyNode(item);
      const subscriptionId = subscription.subscription_id || "";
      const isActive = isActiveSubscription(subscription.status_active);

      return {
        key: subscription.subscription_id || `subscription-${index}`,
        subscriptionId,
        propertyAddress: property.address?.full_address || "Property",
        propertyId: property.property_id || "-",
        isActive,
        isCanceling: cancelingId === subscriptionId,
        renewalDateLabel: formatDate(subscription.current_period_end),
        annualFeeLabel: formatAnnualFee(subscription.subscription_amount),
        onCancel: async () => {
          if (!subscriptionId || !isActive) {
            return;
          }
          await cancelSubscription(subscriptionId);
        },
      };
    });
  }, [subscriptions, cancelingId, cancelSubscription]);

  return {
    loading,
    cards,
  };
};
